import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { User, Mail, Phone, Lock, Eye, EyeOff, XCircle, ArrowRight } from "lucide-react";
import { controlLoading } from "@/services/slices/app.slice";
import { sendBusinessEmailVerificationCodeAPI } from "@/services/api/user/businessSignup";
import Modal from "@/components/Modal";
import {
  AUTH_CARD_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
  AUTH_SELECT_CLASS,
  authInputClass,
} from "@/components/auth/authStyles";
import { COUNTRY_CODES } from "@/assets/countryCodes";
import {
  buildBusinessSignupQuery,
  readBusinessSignupParams,
} from "./businessSignupState";

type FieldKey =
  | "username"
  | "companyEmail"
  | "phoneNum"
  | "password"
  | "confirmedPassword";

const FIELD_ORDER: FieldKey[] = [
  "username",
  "companyEmail",
  "phoneNum",
  "password",
  "confirmedPassword",
];

const splitPhoneParam = (value?: string) => {
  const [code, ...rest] = (value || "").trim().split(" ");
  return {
    countryCode: COUNTRY_CODES.some((c) => c.value === code) ? code : "+852",
    phoneNum: rest.join(" "),
  };
};

const BusinessSignupPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const initial = readBusinessSignupParams(search);
  const initialPhone = splitPhoneParam(initial.phone);

  const [username, setUsername] = useState(initial.username || "");
  const [companyEmail, setCompanyEmail] = useState(initial.companyEmail || "");
  const [phoneNum, setPhoneNum] = useState(initialPhone.phoneNum);
  const [countryCode, setCountryCode] = useState(() => {
    const stored = initial.regionCode;
    return stored && COUNTRY_CODES.some((c) => c.value === stored)
      ? stored
      : initialPhone.countryCode;
  });
  const [password, setPassword] = useState(initial.password || "");
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [modalAlertVisible, setModalAlertVisible] = useState(false);
  const [modalAlertText, setModalAlertText] = useState("");

  const usernameRef = useRef<HTMLInputElement>(null);
  const companyEmailRef = useRef<HTMLInputElement>(null);
  const phoneNumRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmedPasswordRef = useRef<HTMLInputElement>(null);

  const FIELD_REFS: Record<FieldKey, React.RefObject<HTMLInputElement>> = {
    username: usernameRef,
    companyEmail: companyEmailRef,
    phoneNum: phoneNumRef,
    password: passwordRef,
    confirmedPassword: confirmedPasswordRef,
  };

  const showAlert = (text: string) => {
    setModalAlertText(t(text));
    setModalAlertVisible(true);
  };

  const handleChange = (
    key: FieldKey,
    value: string,
    setter: (value: string) => void,
  ) => {
    setter(value);
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleRegister = async () => {
    const errors: Partial<Record<FieldKey, string>> = {};
    if (!username) errors.username = "Please fill in all the blank";
    if (!companyEmail) errors.companyEmail = "Please fill in all the blank";
    if (!phoneNum.trim()) errors.phoneNum = "Please fill in all the blank";
    if (!password) errors.password = "Please fill in all the blank";
    if (!confirmedPassword) errors.confirmedPassword = "Please fill in all the blank";
    if (password && password.length < 8) {
      errors.password = "Password must be at least 8 digits!";
    }
    if (password && confirmedPassword && password !== confirmedPassword) {
      errors.confirmedPassword = "Password and Confirmed Password are not match";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstInvalid = FIELD_ORDER.find((key) => errors[key]);
      if (firstInvalid) FIELD_REFS[firstInvalid].current?.focus();
      return;
    }
    setFieldErrors({});
    dispatch(controlLoading({ showLoading: true }));
    const resp = await sendBusinessEmailVerificationCodeAPI(username, companyEmail);
    dispatch(controlLoading({ showLoading: false }));
    if (!resp || resp.success === false || resp.status === 2) {
      setModalAlertText(
        typeof resp?.result === "string" && resp.result
          ? resp.result
          : t("Network error, please try again"),
      );
      return setModalAlertVisible(true);
    }
    if (resp?.status === -20000) return showAlert("signup.businessEmailExisted");
    if (resp?.status === -20001) return showAlert("signup.usernameExisted");
    if (resp?.status === 0) {
      navigate(
        `/business/signup/verify${buildBusinessSignupQuery({
          username,
          companyEmail,
          phone: `${countryCode} ${phoneNum.trim()}`,
          regionCode: countryCode,
          password,
        })}`,
      );
    }
  };

  const fieldError = (key: FieldKey) => {
    const message = fieldErrors[key];
    if (!message) return null;
    return (
      <p className="mt-1.5 flex items-center gap-1 text-[13px] text-red-500">
        <XCircle size={14} className="shrink-0" />
        {t(message)}
      </p>
    );
  };

  return (
    <div className={`${AUTH_CARD_CLASS} overflow-hidden`}>
      {/* Header — same heading block the login page uses. */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#333]">
          {t("Create Business Account")}
        </h1>
        <p className="mt-1.5 text-sm text-[#333]/45">
          {t("Create your account with your company email.")}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="business-signup-username"
            className={AUTH_LABEL_CLASS}
          >
            {t("Username")}
          </label>
          <div className="relative">
            <User
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="business-signup-username"
              ref={usernameRef}
              type="text"
              value={username}
              autoComplete="username"
              onChange={(e) => handleChange("username", e.target.value, setUsername)}
              className={authInputClass(!!fieldErrors.username)}
              placeholder={t("Username")}
              aria-invalid={!!fieldErrors.username}
            />
          </div>
          {fieldError("username")}
        </div>
        <div>
          <label
            htmlFor="business-signup-email"
            className={AUTH_LABEL_CLASS}
          >
            {t("Company Email")}
          </label>
          <div className="relative">
            <Mail
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="business-signup-email"
              ref={companyEmailRef}
              type="email"
              value={companyEmail}
              autoComplete="email"
              onChange={(e) => handleChange("companyEmail", e.target.value, setCompanyEmail)}
              className={authInputClass(!!fieldErrors.companyEmail)}
              placeholder="you@company.com"
              aria-invalid={!!fieldErrors.companyEmail}
            />
          </div>
          {fieldError("companyEmail")}
        </div>
        <div>
          <label
            htmlFor="business-signup-phone"
            className={AUTH_LABEL_CLASS}
          >
            {t("Phone Number")}
          </label>
          <div className="flex gap-2">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className={`${AUTH_SELECT_CLASS} w-28`}
              autoComplete="tel-country-code"
              aria-label={t("Region Code")}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.flag} {c.value}
                </option>
              ))}
            </select>
            <div className="relative flex-1">
              <Phone
                size={18}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                id="business-signup-phone"
                ref={phoneNumRef}
                type="tel"
                value={phoneNum}
                autoComplete="tel-national"
                onChange={(e) => handleChange("phoneNum", e.target.value, setPhoneNum)}
                className={authInputClass(!!fieldErrors.phoneNum)}
                placeholder="0000 0000"
                aria-invalid={!!fieldErrors.phoneNum}
              />
            </div>
          </div>
          {fieldError("phoneNum")}
        </div>
        <div>
          <label
            htmlFor="business-signup-password"
            className={AUTH_LABEL_CLASS}
          >
            {t("Password")}
          </label>
          <div className="relative">
            <Lock
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="business-signup-password"
              ref={passwordRef}
              type={showPassword ? "text" : "password"}
              value={password}
              autoComplete="new-password"
              onChange={(e) => handleChange("password", e.target.value, setPassword)}
              className={authInputClass(!!fieldErrors.password, true)}
              placeholder="••••••••"
              aria-invalid={!!fieldErrors.password}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t("Hide password") : t("Show password")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-[#ffa217]"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {fieldError("password")}
        </div>
        <div>
          <label
            htmlFor="business-signup-confirmed-password"
            className={AUTH_LABEL_CLASS}
          >
            {t("Confirmed Password")}
          </label>
          <div className="relative">
            <Lock
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="business-signup-confirmed-password"
              ref={confirmedPasswordRef}
              type={showConfirmedPassword ? "text" : "password"}
              value={confirmedPassword}
              autoComplete="new-password"
              onChange={(e) =>
                handleChange("confirmedPassword", e.target.value, setConfirmedPassword)
              }
              onKeyDown={(e) => e.key === "Enter" && handleRegister()}
              className={authInputClass(!!fieldErrors.confirmedPassword, true)}
              placeholder="••••••••"
              aria-invalid={!!fieldErrors.confirmedPassword}
            />
            <button
              type="button"
              onClick={() => setShowConfirmedPassword((v) => !v)}
              aria-label={
                showConfirmedPassword ? t("Hide password") : t("Show password")
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-[#ffa217]"
            >
              {showConfirmedPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {fieldError("confirmedPassword")}
        </div>

        <button
          type="button"
          onClick={handleRegister}
          className={`group flex items-center justify-center gap-2 ${AUTH_PRIMARY_BUTTON_CLASS}`}
        >
          {t("REGISTER")}
          <ArrowRight
            size={16}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </button>
      </div>

      <Modal visible={modalAlertVisible} onClose={() => setModalAlertVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">{modalAlertText}</div>
          <button
            type="button"
            onClick={() => setModalAlertVisible(false)}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessSignupPage;
